import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import NavigationBar from '../../components/global/Layout/MainLayout/NavigationBar';
import { ActionPaper } from '../../components/global/Widget';
import { BasicTable } from '../../components/global/table';
import { useNavigate } from 'react-router-dom';
import { useGetCustomersBillingSummary } from '../../hook/query/useBilling';
import { IBillingCustomerSummary } from '../../compiler/billing.interface';

const formatCurrency = (amount: number) =>
  Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getPaymentStatus = (customer: IBillingCustomerSummary) => {
  if (Number(customer.outstanding_amount || 0) <= 0) return 'paid';
  if (Number(customer.paid_amount || 0) > 0) return 'partial';
  return 'pending';
};

const getStatusChip = (customer: IBillingCustomerSummary) => {
  const status = getPaymentStatus(customer);
  if (status === 'paid') return <Chip label="ชำระครบ" size="small" color="success" />;
  if (status === 'partial') return <Chip label="ชำระบางส่วน" size="small" color="warning" />;
  return <Chip label="ค้างชำระ" size="small" color="error" variant="outlined" />;
};

const CustomerBillingPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'all' | 'has_outstanding' | 'completed'>('all');

  const { data: customersResponse, isLoading } = useGetCustomersBillingSummary({
    search: search.trim() || undefined,
    status_filter: statusFilter,
    enforce_customer_scope: true,
  });

  const customers = customersResponse?.data?.customers ?? [];
  const summary = React.useMemo(
    () =>
      customers.reduce(
        (acc, item) => ({
          totalCustomers: acc.totalCustomers + 1,
          totalInvoices: acc.totalInvoices + Number(item.invoice_count || 0),
          totalAmount: acc.totalAmount + Number(item.total_amount || 0),
          paidAmount: acc.paidAmount + Number(item.paid_amount || 0),
          outstandingAmount: acc.outstandingAmount + Number(item.outstanding_amount || 0),
        }),
        {
          totalCustomers: 0,
          totalInvoices: 0,
          totalAmount: 0,
          paidAmount: 0,
          outstandingAmount: 0,
        },
      ),
    [customers],
  );

  const openCustomerBilling = (customer: IBillingCustomerSummary) => {
    const params = new URLSearchParams({
      customerNo: customer.customer_code,
      customerName: customer.customer_name || '',
    });
    navigate(`/customer-portal/invoice-list/billing-invoices?${params.toString()}`);
  };

  const tableHeaders = [
    { name: '#', props: { align: 'center' as const, width: '5%' } },
    { name: 'รหัสลูกค้า', props: { align: 'left' as const, width: '12%' } },
    { name: 'ชื่อบริษัท', props: { align: 'left' as const, width: '25%' } },
    { name: 'รอบทั้งหมด', props: { align: 'center' as const, width: '9%' } },
    { name: 'Invoice', props: { align: 'center' as const, width: '9%' } },
    { name: 'ยอดรวม (บาท)', props: { align: 'right' as const, width: '13%' } },
    { name: 'ชำระแล้ว (บาท)', props: { align: 'right' as const, width: '13%' } },
    { name: 'ค้างชำระ (บาท)', props: { align: 'right' as const, width: '13%' } },
    { name: 'สถานะ', props: { align: 'center' as const, width: '8%' } },
    { name: 'เครื่องมือ', props: { align: 'center' as const, width: '8%' } },
  ];

  return (
    <>
      <NavigationBar title="รายการวางบิล" />

      <Stack spacing={2} sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Paper sx={{ flex: 1, p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <BusinessIcon color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  บริษัท
                </Typography>
                <Typography variant="h5" color="primary" fontWeight={700}>
                  {summary.totalCustomers.toLocaleString()}
                </Typography>
              </Box>
            </Stack>
          </Paper>
          <Paper sx={{ flex: 1, p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ReceiptLongIcon color="success" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Invoice
                </Typography>
                <Typography variant="h5" color="success.main" fontWeight={700}>
                  {summary.totalInvoices.toLocaleString()}
                </Typography>
              </Box>
            </Stack>
          </Paper>
          <Paper sx={{ flex: 1, p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <PaidIcon color="error" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  ค้างชำระ
                </Typography>
                <Typography variant="h5" color="error.main" fontWeight={700}>
                  {formatCurrency(summary.outstandingAmount)}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Stack>

        <ActionPaper>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ p: 2 }}>
            <TextField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              size="small"
              label="ค้นหารหัส / ชื่อบริษัท"
              sx={{ minWidth: 280 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>สถานะ</InputLabel>
              <Select
                value={statusFilter}
                label="สถานะ"
                onChange={(event) =>
                  setStatusFilter(event.target.value as 'all' | 'has_outstanding' | 'completed')
                }
              >
                <MenuItem value="all">ทั้งหมด</MenuItem>
                <MenuItem value="has_outstanding">ค้างชำระ</MenuItem>
                <MenuItem value="completed">ชำระครบ</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {customers.length === 0 && !isLoading && (
            <Box sx={{ px: 2, pb: 2 }}>
              <Alert severity="info">
                ไม่พบรายการวางบิล หรือผู้ใช้งานนี้ยังไม่ได้ถูกผูกกับบริษัทลูกค้า
              </Alert>
            </Box>
          )}

          <Box id="customer-billing-table">
            <BasicTable
              headers={tableHeaders}
              isLoading={isLoading}
              limit={customers.length || 10}
              rows={customers.map((customer, index) => [
                { ele: index + 1, props: { align: 'center' as const } },
                {
                  ele: (
                    <Chip
                      label={customer.customer_code}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ fontWeight: 700 }}
                    />
                  ),
                  props: { align: 'left' as const },
                },
                { ele: customer.customer_name || '-', props: { align: 'left' as const } },
                { ele: Number(customer.total_rounds || 0).toLocaleString(), props: { align: 'center' as const } },
                { ele: Number(customer.invoice_count || 0).toLocaleString(), props: { align: 'center' as const } },
                { ele: formatCurrency(customer.total_amount), props: { align: 'right' as const } },
                {
                  ele: formatCurrency(customer.paid_amount),
                  props: { align: 'right' as const, sx: { color: 'success.main', fontWeight: 600 } },
                },
                {
                  ele: formatCurrency(customer.outstanding_amount),
                  props: {
                    align: 'right' as const,
                    sx: {
                      color: Number(customer.outstanding_amount || 0) > 0 ? 'error.main' : 'text.primary',
                      fontWeight: 700,
                    },
                  },
                },
                { ele: getStatusChip(customer), props: { align: 'center' as const } },
                {
                  ele: (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon fontSize="small" />}
                      onClick={() => openCustomerBilling(customer)}
                    >
                      ดูบิล
                    </Button>
                  ),
                  props: { align: 'center' as const },
                },
              ])}
            />
          </Box>
        </ActionPaper>
      </Stack>
    </>
  );
};

export default CustomerBillingPage;
